import { HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';

export class HttpHelper {
  /**
   * Converts a value to a string suitable for HTTP parameters.
   * @param value : any - value to parse
   * @returns : string | null - parsed value or null if value is invalid to be parsed.
   */
  private static parseValue(value: any): string | null {
    if (
      value === null ||
      value === undefined ||
      typeof value === 'function' ||
      (typeof value === 'string' && value.trim() === '')
    )
      return null;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
  }

  /**
   * Transforms an object into HttpParams.
   * @param obj : Record<string, any> - object to be converted to HttpParams
   * @returns : HttpParams - converted HttpParams
   */
  static toHttpParams(
    obj?: Record<string, any>,
    useCommaForArrays = false
  ): HttpParams {
    if (!obj) return new HttpParams();

    let params = new HttpParams();

    Object.keys(obj).forEach((key) => {
      const value = obj[key];

      if (Array.isArray(value)) {
        if (useCommaForArrays) {
          const parsedArray = value
            .map((v) => this.parseValue(v))
            .filter((v): v is string => v !== null);

          if (parsedArray.length === 0) return;

          params = params.append(key, parsedArray.join(','));

          return;
        } else {
          value.forEach((v) => {
            const parsedV = this.parseValue(v);

            if (parsedV === null) return;

            params = params.append(key, parsedV);
          });
        }

        return;
      }

      const parsedValue = this.parseValue(value);
      if (parsedValue === null) return;
      params = params.append(key, parsedValue);
    });

    return params;
  }

  /**
   * add base to url
   *
   * @param url - string, example: /art/index.html
   * @param host - string : base host, example: https://www.example.com
   * @returns - string: Final result: https://www.example.com/art/index.html
   */
  static prepareUrl(url: string, host: string): string {
    //Check if it is an empty string
    if (!url) return url;
    const absolutePattern = /^http(s)?:\/\//i;
    // If it's already absolute, return as is. Absolute means it starts with http:// or https://
    url = absolutePattern.test(url) ? url : host + '/' + url;
    return url.replace(/([^:]\/)\/+/g, '$1');
  }

  /**
   * Adds headers to a request if they are missing.
   * @param req : HttpRequest<T> - original request
   * @param headersToAdd : Record<string, string> - headers to add if they are missing
   * @returns Request<T> - new request with the missing headers added
   */
  static addMissingHeaders<T>(
    req: HttpRequest<T>,
    headersToAdd: Record<string, string>
  ): HttpRequest<T> {
    let headers: HttpHeaders = req.headers;

    for (const [key, value] of Object.entries(headersToAdd)) {
      if (!headers.has(key)) {
        headers = headers.set(key, value);
      }
    }

    return req.clone({ headers });
  }

  /**
   * Returns true if the given URL ends with the specified endpoint, ignoring query parameters and fragments.
   * Examples:
   * isEndpoint('https://api.example.com/login', 'login') => true
   * isEndpoint('https://api.example.com/login?redirect=home', 'login') => true
   * isEndpoint('https://api.example.com/login#section', 'login') => true
   * isEndpoint('https://api.example.com/user/profile', 'login') => false
   * @param url : string - full url
   * @param endpoint : string - endpoint to check
   * @returns boolean - true if the url ends with the endpoint, ignoring query parameters and fragments
   */
  static isEndpoint(url: string, endpoint: string): boolean {
    try {
      const cleanUrl = url.split('?')[0].split('#')[0];

      const normalized = cleanUrl.toLowerCase();

      const trimmed = normalized.replace(/\/+$/, '');

      return trimmed.endsWith(`/${endpoint.toLowerCase()}`);
    } catch {
      return false;
    }
  }
}
